import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Offcanvas } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Select from "react-select";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";
import CustomTextAreaWithLabel from "src/layouts/form/CustomTextAreaWithLabel";
import {
  AirlineRef,
  BookingFlight,
  BookingFlightEditForm,
  FLIGHT_STATUS_LABEL,
  editFlightBooking,
  searchAirlinesRef,
} from "src/store/reducers/bookings";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import CustomDatePickerWithLabel from "src/layouts/form/CustomDateTimeInputWithLabel";
import { KeyRequired } from "src/layouts/form/validations/Validation";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";
import CustomSelectWithLabel from "src/layouts/form/CustomSelectWithLabel";
import CustomReactSelectWithLabel from "src/layouts/form/CustomReactSelectWithLabel";
import { fetchListAgents } from "src/store/reducers/agents";

type IEditBookingFlightCanvas = {
  Title: string;
  editId: string;
  hide: () => void;
  data: BookingFlight;
};

const EditBookingFlightCanvas = ({
  Title,
  editId,
  hide,
  data,
}: IEditBookingFlightCanvas) => {
  const [pending, setPending] = useState(false);
  const [airlineOptions, setAirlineOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [airlineInputValue, setAirlineInputValue] = useState("");
  const [airlineSearchStatus, setAirlineSearchStatus] = useState<
    "idle" | "pending"
  >("idle");
  const abortRef = useRef<AbortController>();
  const dispatch = useAppDispatch();
  const agentsList = useAppSelector((state) => state.agents.list.data);

  useEffect(() => {
    if (!data) hide();
  }, [data, hide]);

  const {
    handleChange,
    values,
    handleSubmit,
    resetForm,
    setFieldValue,
    touched,
    setErrors,
    errors,
  } = useFormik<{
    airline_name: string;
    flight_number: string;
    departure_airport: string;
    arrival_airport: string;
    departure_datetime: string;
    arrival_datetime: string;
    flight_class: string;
    fare: string;
    company_fare: string;
    gst: string;
    pnr_number: string;
    flight_status: number;
    advance_amount_paid: string;
    payment_notification_date: string;
    additional_notes: string;
    travel_agent: string;
  }>({
    initialValues: {
      airline_name: data?.airline_name || "",
      flight_number: data?.flight_number || "",
      departure_airport: data?.departure_airport || "",
      arrival_airport: data?.arrival_airport || "",
      departure_datetime: data?.departure_datetime || "",
      arrival_datetime: data?.arrival_datetime || "",
      flight_class: data?.flight_class || "",
      fare: data?.fare || "",
      company_fare: data?.company_fare || "",
      gst: data?.gst || "",
      pnr_number: data?.pnr_number || "",
      flight_status: data?.flight_status || 1,
      advance_amount_paid: data?.advance_amount_paid || "",
      payment_notification_date: data?.payment_notification_date || "",
      additional_notes: data?.additional_notes || "",
      travel_agent: data?.travel_agent?.id || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      departure_datetime: KeyRequired("Departure date time"),
      arrival_datetime: KeyRequired("Arrival date time"),
      fare: Yup.number()
        .typeError("Customer Fare must be a number")
        .required("Customer Fare is required")
        .positive("Customer Fare must be positive")
        .integer("Customer Fare must be an integer"),
      company_fare: Yup.number()
        .typeError("Company Fare must be a number")
        .positive("Company Fare must be positive")
        .integer("Company Fare must be an integer"),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      setPending(true);
      editFlight({ ...values, id: editId, booking: data.booking, travel_agent: values.travel_agent || null });
    },
  });

  const editFlight = useCallback(
    async (values: BookingFlightEditForm) => {
      try {
        await dispatch(editFlightBooking(values)).unwrap();
        toast.success("Flight updated successfully");
        hide();
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [dispatch, hide, setErrors]
  );

  const handleAirlineSearch = useCallback(
    async (inputValue: string) => {
      if (abortRef.current) abortRef.current.abort();
      if (!inputValue) {
        setAirlineOptions([]);
        setAirlineSearchStatus("idle");
        return;
      }
      setAirlineSearchStatus("pending");
      try {
        abortRef.current = new AbortController();
        const result: AirlineRef[] = await dispatch(
          searchAirlinesRef(inputValue)
        ).unwrap();
        setAirlineOptions(
          result.map((a) => ({
            label: `${a.name} (${a.iata_code})`,
            value: a.name,
          }))
        );
      } catch (_) {
        // silently fail
      } finally {
        setAirlineSearchStatus("idle");
      }
    },
    [dispatch]
  );

  const handleAirlineInputChange = useCallback((inputValue: string) => {
    setAirlineInputValue(inputValue);
    void handleAirlineSearch(inputValue);
    return inputValue;
  }, [handleAirlineSearch]);

  useEffect(() => {
    resetForm();
    setAirlineOptions([]);
    setAirlineInputValue("");
    if (editId) {
      dispatch(fetchListAgents());
    }
  }, [resetForm, editId, dispatch]);

  return (
    <Offcanvas
      show={!!editId}
      onHide={hide}
      className="offcanvas-end customeoff"
      placement="end"
    >
      <div className="offcanvas-header">
        <h3 className="modal-title" id="#gridSystemModal">
          {Title}
        </h3>
        <button type="button" className="btn-close" onClick={hide}>
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
      <div className="offcanvas-body">
        <div className="container-fluid">
          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* Airline search */}
              <div className="col-xl-6 mb-3">
                <label className="form-label">Airline</label>
                <Select
                  isClearable
                  placeholder="Search airline name or code..."
                  options={airlineOptions}
                  isLoading={airlineSearchStatus === "pending"}
                  inputValue={airlineInputValue}
                  onInputChange={handleAirlineInputChange}
                  onChange={(e) => {
                    setFieldValue("airline_name", e?.value || "");
                    setAirlineInputValue("");
                  }}
                  value={
                    values.airline_name
                      ? airlineOptions.find(
                          (o) => o.value === values.airline_name
                        ) || {
                          label: values.airline_name,
                          value: values.airline_name,
                        }
                      : null
                  }
                  filterOption={() => true}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue ? "No airlines found" : "Type to search"
                  }
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="flight_number"
                  labelText="Flight Number"
                  handleChange={handleChange}
                  value={values.flight_number}
                  touched={touched.flight_number}
                  errors={errors.flight_number}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="departure_airport"
                  labelText="Departure Airport"
                  handleChange={handleChange}
                  value={values.departure_airport}
                  touched={touched.departure_airport}
                  errors={errors.departure_airport}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="arrival_airport"
                  labelText="Arrival Airport"
                  handleChange={handleChange}
                  value={values.arrival_airport}
                  touched={touched.arrival_airport}
                  errors={errors.arrival_airport}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomDatePickerWithLabel
                  id="departure_datetime"
                  labelText="Departure Date & Time"
                  required={true}
                  pickerType="datetime"
                  handleChange={(date) =>
                    setFieldValue("departure_datetime", date)
                  }
                  value={values.departure_datetime}
                  selectsStart
                  endDate={values.arrival_datetime}
                  touched={touched.departure_datetime}
                  errors={errors.departure_datetime}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomDatePickerWithLabel
                  id="arrival_datetime"
                  labelText="Arrival Date & Time"
                  required={true}
                  pickerType="datetime"
                  handleChange={(date) =>
                    setFieldValue("arrival_datetime", date)
                  }
                  value={values.arrival_datetime}
                  selectsEnd
                  startDate={values.departure_datetime}
                  touched={touched.arrival_datetime}
                  errors={errors.arrival_datetime}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="flight_class"
                  labelText="Class"
                  handleChange={handleChange}
                  value={values.flight_class}
                  touched={touched.flight_class}
                  errors={errors.flight_class}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="fare"
                  labelText="Customer Fare"
                  required={true}
                  handleChange={handleChange}
                  value={values.fare}
                  touched={touched.fare}
                  errors={errors.fare}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="company_fare"
                  labelText="Company Fare"
                  handleChange={handleChange}
                  value={values.company_fare}
                  touched={touched.company_fare}
                  errors={errors.company_fare}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomReactSelectWithLabel
                  labelText="Travel Agent"
                  placeholder="Travel Agent"
                  id="travel_agent"
                  options={agentsList.map((agent) => ({
                    label: agent.name,
                    value: agent.id,
                  }))}
                  handleChange={handleChange}
                  touched={touched.travel_agent}
                  errors={errors.travel_agent}
                  value={
                    agentsList
                      .filter((agent) => agent.id === values.travel_agent)
                      .map((agent) => ({ label: agent.name, value: agent.id }))[0] || null
                  }
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="gst"
                  labelText="GST"
                  handleChange={handleChange}
                  value={values.gst}
                  touched={touched.gst}
                  errors={errors.gst}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="pnr_number"
                  labelText="PNR Number"
                  handleChange={handleChange}
                  value={values.pnr_number}
                  touched={touched.pnr_number}
                  errors={errors.pnr_number}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="advance_amount_paid"
                  labelText="Advance Amount Paid"
                  handleChange={handleChange}
                  value={values.advance_amount_paid}
                  touched={touched.advance_amount_paid}
                  errors={errors.advance_amount_paid}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomDatePickerWithLabel
                  id="payment_notification_date"
                  labelText="Payment Notification Date"
                  pickerType="YYYY-MM-DD"
                  handleChange={(date) =>
                    setFieldValue("payment_notification_date", date)
                  }
                  value={values.payment_notification_date}
                  touched={touched.payment_notification_date}
                  errors={errors.payment_notification_date}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomSelectWithLabel
                  id="flight_status"
                  labelText="Flight Status"
                  handleChange={handleChange}
                  value={values.flight_status}
                  touched={touched.flight_status}
                  errors={errors.flight_status}
                >
                  {Object.entries(FLIGHT_STATUS_LABEL).map(([key, label]) => (
                    <option value={key} key={key}>
                      {label}
                    </option>
                  ))}
                </CustomSelectWithLabel>
              </div>
              <div className="col-xl-12 mb-3">
                <CustomTextAreaWithLabel
                  id="additional_notes"
                  labelText="Additional Notes"
                  handleChange={handleChange}
                  value={values.additional_notes}
                  touched={touched.additional_notes}
                  errors={errors.additional_notes}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={pending}
              className="btn btn-primary me-1"
            >
              {pending ? "Saving..." : "Submit"}
            </button>
            <Link to={"#"} onClick={hide} className="btn btn-danger light ms-1">
              Cancel
            </Link>
          </form>
        </div>
      </div>
    </Offcanvas>
  );
};

export default EditBookingFlightCanvas;
